// Dependencies
import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { PageShell, PageHero } from "@/components/PageShell";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";

// Icons
import { Mail, Phone, MapPin } from "lucide-react";
import { FaWhatsapp, FaInstagram, FaYoutube, FaLinkedin } from "react-icons/fa";

// Images
import heroImage from "@/assets/hero-contact.avif";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AsYouType, CountryCode } from "libphonenumber-js";

const COUNTRIES = [
  { code: "BR", name: "Brasil", dialCode: "+55", flag: "🇧🇷", placeholder: "(11) 99999-9999" },
  { code: "US", name: "Estados Unidos", dialCode: "+1", flag: "🇺🇸", placeholder: "(201) 555-0123" },
  { code: "GB", name: "Reino Unido", dialCode: "+44", flag: "🇬🇧", placeholder: "7911 123456" },
  { code: "FR", name: "França", dialCode: "+33", flag: "🇫🇷", placeholder: "06 12 34 56 78" },
  { code: "DE", name: "Alemanha", dialCode: "+49", flag: "🇩🇪", placeholder: "0151 23456789" },
  { code: "ES", name: "Espanha", dialCode: "+34", flag: "🇪🇸", placeholder: "612 345 678" },
  { code: "IT", name: "Itália", dialCode: "+39", flag: "🇮🇹", placeholder: "312 345 6789" },
  { code: "PT", name: "Portugal", dialCode: "+351", flag: "🇵🇹", placeholder: "912 345 678" },
  { code: "AR", name: "Argentina", dialCode: "+54", flag: "🇦🇷", placeholder: "(11) 1234-5678" },
  { code: "CL", name: "Chile", dialCode: "+56", flag: "🇨🇱", placeholder: "9 1234 5678" },
  { code: "IN", name: "Índia", dialCode: "+91", flag: "🇮🇳", placeholder: "91234 56789" },
  { code: "JP", name: "Japão", dialCode: "+81", flag: "🇯🇵", placeholder: "090-1234-5678" },
];

export const Route = createFileRoute("/$lang/contact")({
  head: () => ({
    meta: [
      { title: i18n.t("contact:title", "Contact") + " — BIZU Space" },
      {
        name: "description",
        content: i18n.t("contact:metaDesc",
          "Get in touch with BIZU Space — business@bizu.space · +55 12 98238-3191 · São José dos Campos, Brazil.",
        ),
      },
      { property: "og:title", content: i18n.t("contact:title", "Contact") + " — BIZU Space" },
      { property: "og:description", content: i18n.t("contact:description", "Get in touch with BIZU Space.") },
      { property: "og:url", content: "/contact" },
    ],
    links: [{ rel: "canonical", href: "https://bizu.space/contact" }],
  }),
  component: Contact,
});

function Contact() {
  const { t } = useTranslation("contact");
  const [submitted, setSubmitted] = useState(false);
  const [phone, setPhone] = useState("");
  const [country, setCountry] = useState<CountryCode>("BR");
  const [isSending, setIsSending] = useState(false);
  const [formError, setFormError] = useState("");

  const formatPhone = (value: string, currentCountry: CountryCode) => {
    if (currentCountry === "BR") {
      const numbers = value.replace(/\D/g, "").slice(0, 11);
      if (!numbers) return "";
      if (numbers.length <= 10) {
        return numbers.replace(/^(\d{2})(\d)/, "($1) $2").replace(/(\d{4})(\d)/, "$1-$2");
      }
      return numbers.replace(/^(\d{2})(\d)/, "($1) $2").replace(/(\d{5})(\d)/, "$1-$2");
    }

    const formatter = new AsYouType(currentCountry);
    return formatter.input(value);
  };

  const handleSubmit = async (event: React.SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();

    setIsSending(true);
    setFormError("");

    const form = event.currentTarget;
    const formData = new FormData(form);

    try {
      const response = await fetch("https://emailsite.compras-ac0.workers.dev/", {
        method: "POST",
        body: formData,
      });

      if (response.ok) {
        setSubmitted(true);
        form.reset();
        setPhone("");
      } else {
        setFormError(t("errorMsg", "Unable to send your message right now. Please try again."));
      }
    } catch {
      setFormError(t("errorMsg", "Unable to send your message right now. Please try again."));
    } finally {
      setIsSending(false);
    }
  };

  return (
    <PageShell>
      <PageHero
        title={t("title", "Contact")}
        description={t("description", "Get in touch with BIZU Space and let's build the future of space together.")}
        image={heroImage}
      />

      {/* Contact section */}
      <section className="container-bizu grid gap-14 py-20 lg:grid-cols-[1fr_1fr]">
        {/* Info */}
        <div className="min-w-0">
          <h2 className="max-w-2xl text-4xl font-black uppercase leading-tight text-foreground md:text-5xl">
            {t("infoTitle", "Let's build the future of space together")}
          </h2>

          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-muted-foreground">
            {t("infoDesc", "Have a project, partnership, or technical demand? Our team is ready to collaborate and deliver high-performance aerospace solutions tailored to your mission.")}
          </p>

          <div className="mt-12 divide-y divide-border border-y border-border">
            <div className="grid gap-4 py-7 sm:grid-cols-[220px_1fr]">
              <h3 className="text-xl font-bold text-foreground">{t("social", "Social media")}</h3>

              <div className="flex flex-wrap items-center gap-3">
                <a
                  href="https://www.linkedin.com/company/bizu-space"
                  aria-label="LinkedIn"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-12 w-12 items-center justify-center rounded-full border border-white/40 text-white transition-all duration-300 hover:border-accent hover:text-accent"
                >
                  <FaLinkedin size={22} />
                </a>

                <a
                  href="https://www.instagram.com/bizu.space"
                  aria-label="Instagram"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-12 w-12 items-center justify-center rounded-full border border-white/40 text-white transition-all duration-300 hover:border-accent hover:text-accent"
                >
                  <FaInstagram size={22} />
                </a>

                <a
                  href="https://www.youtube.com/@bizuspace"
                  aria-label="YouTube"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-12 w-12 items-center justify-center rounded-full border border-white/40 text-white transition-all duration-300 hover:border-accent hover:text-accent"
                >
                  <FaYoutube size={24} />
                </a>

                <a
                  href="https://api.whatsapp.com/send?phone=5512982383191"
                  aria-label="WhatsApp"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-12 w-12 items-center justify-center rounded-full border border-white/40 text-white transition-all duration-300 hover:border-accent hover:text-accent"
                >
                  <FaWhatsapp size={24} />
                </a>
              </div>
            </div>

            <div className="grid gap-4 py-7 sm:grid-cols-[220px_1fr]">
              <h3 className="flex items-center gap-2 text-xl font-bold text-foreground">
                <MapPin size={20} />
                {t("location", "Location")}
              </h3>

              <a
                href="https://maps.app.goo.gl/pdNKFDDvVWSe7nBt8"
                className="text-lg text-muted-foreground transition hover:text-accent break-words min-w-0"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("address", "Parque Tecnológico Univap - S101-A Av. Shishima Hifumi, 2911, Urbanova São José dos Campos-SP, 12244-000, Brazil")}
              </a>
            </div>

            <div className="grid gap-4 py-7 sm:grid-cols-[220px_1fr]">
              <h3 className="flex items-center gap-2 text-xl font-bold text-foreground">
                <Mail size={20} />
                {t("email", "Email")}
              </h3>

              <a
                href="mailto:business@bizu.space"
                className="text-lg text-muted-foreground transition hover:text-accent break-all min-w-0"
                target="_blank"
                rel="noopener noreferrer"
              >
                business@bizu.space
              </a>
            </div>

            <div className="grid gap-4 py-7 sm:grid-cols-[220px_1fr]">
              <h3 className="flex items-center gap-2 text-xl font-bold text-foreground">
                <Phone size={20} />
                {t("phone", "Phone")}
              </h3>

              <a
                href="https://api.whatsapp.com/send?phone=5512982383191"
                className="text-lg text-muted-foreground transition hover:text-accent break-words min-w-0"
                target="_blank"
                rel="noopener noreferrer"
              >
                +55 12 98238-3191
              </a>
            </div>
          </div>
        </div>

        {/* Form */}
        {submitted ? (
          <div className="flex min-h-[500px] flex-col items-center justify-center rounded-3xl border border-border bg-black/40 p-8 text-center md:p-10">
            <h3 className="text-3xl font-bold text-accent">{t("successTitle", "Message sent successfully!")}</h3>

            <p className="mt-4 max-w-md text-muted-foreground">
              {t("successDesc", "Thank you for contacting BIZU Space. Our team will review your message and get back to you as soon as possible.")}
            </p>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="rounded-3xl border border-border bg-black/40 p-5 sm:p-8 shadow-sm md:p-10 min-w-0"
          >
            <input type="hidden" name="subject" value="New Contact Form - BIZU Space" />
            <input type="hidden" name="formType" value="contact" />

            <div className="space-y-7">
              <div>
                <label className="mb-3 flex gap-1 items-center text-lg font-bold text-foreground">
                  {t("labels.name", "Name")} <span className="text-red-500">*</span>
                </label>
                <input
                  name="name"
                  required
                  type="text"
                  placeholder={t("placeholders.name", "Enter your name...")}
                  className="h-14 w-full min-w-0 rounded-full border border-border bg-background px-6 text-foreground placeholder:text-muted-foreground outline-none transition focus:border-accent"
                />
              </div>

              <div>
                <label className="mb-3 flex gap-1 items-center text-lg font-bold text-foreground">
                  {t("labels.email", "Email Address")} <span className="text-red-500">*</span>
                </label>
                <input
                  name="email"
                  required
                  type="email"
                  placeholder={t("placeholders.email", "Enter your e-mail...")}
                  className="h-14 w-full min-w-0 rounded-full border border-border bg-background px-6 text-foreground placeholder:text-muted-foreground outline-none transition focus:border-accent"
                />
              </div>

              <div>
                <label className="mb-3 flex gap-1 items-center text-lg font-bold text-foreground">
                  {t("labels.phone", "Phone Number")} <span className="text-red-500">*</span>
                </label>
                <div className="flex w-full overflow-hidden rounded-full border border-border bg-background focus-within:border-accent transition">
                  <Select
                    value={country}
                    onValueChange={(val) => {
                      const newCountry = val as CountryCode;
                      setCountry(newCountry);
                      setPhone(formatPhone(phone, newCountry));
                    }}
                  >
                    <SelectTrigger className="h-14 w-[100px] rounded-none border-0 border-r border-border bg-transparent shadow-none focus:ring-0">
                      <SelectValue placeholder="DDI" />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.map((c) => (
                        <SelectItem key={c.code} value={c.code}>
                          <span className="flex items-center gap-2">
                            <span>{c.flag}</span>
                            <span>{c.dialCode}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <input
                    name="phone"
                    required
                    type="tel"
                    value={phone}
                    onChange={(event) => setPhone(formatPhone(event.target.value, country))}
                    placeholder={COUNTRIES.find(c => c.code === country)?.placeholder || ""}
                    maxLength={16}
                    className="h-14 min-w-0 flex-1 bg-transparent px-3 sm:px-6 text-foreground placeholder:text-muted-foreground outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="mb-3 flex gap-1 items-center text-lg font-bold text-foreground">
                  {t("labels.message", "Message")} <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="message"
                  required
                  rows={6}
                  placeholder={t("placeholders.message", "Enter your message...")}
                  className="w-full resize-none rounded-[28px] border border-border bg-background px-6 py-5 text-foreground placeholder:text-muted-foreground outline-none transition focus:border-accent"
                />
              </div>

              {formError && <p className="text-sm font-medium text-red-400">{formError}</p>}

              <button
                type="submit"
                disabled={isSending}
                className="h-14 w-full rounded-full bg-accent text-base font-semibold text-black transition hover:bg-accent/80 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSending ? t("buttons.sending", "Sending...") : t("buttons.submit", "Submit")}
              </button>
            </div>
          </form>
        )}
      </section>

      {/* Map */}
      <section className="container-bizu pb-20">
        <div className="overflow-hidden rounded-3xl border border-border">
          <iframe
            title="BIZU Space location"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3666.9512245178817!2d-45.9521379!3d-23.2084481!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x94cc343af4a4d231%3A0xaaad8dfaeb1f0b63!2sParque%20Tecnol%C3%B3gico%20-%20Univap!5e0!3m2!1spt-BR!2sbr!4v1779936403300!5m2!1spt-BR!2sbr"
            width="100%"
            height="450"
            className="block"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </section>
    </PageShell>
  );
}
